import { MIRA_MAX_IMAGE_BYTES, type MiraImageMediaType } from '@lensmira/domain'

import {
  JPEG_QUALITIES,
  MIRA_MAX_EDGE_PX,
  NotCropped,
  cropPixels,
  scaleToFit,
  type CropBox,
  type CropEncoder,
} from './images.js'

/**
 * The browser half of `AI-004`'s crop: decoding a chosen file, showing it, and encoding the region
 * the shopkeeper drew a box around.
 *
 * **Separate from `images.ts` because none of it can run in a test.** jsdom has no image decoding,
 * no canvas 2D context and no `URL.createObjectURL`, so there is no version of this file a suite
 * could exercise. The response is not to mock a canvas and call the result tested: it is to put
 * every decision that can be wrong into `images.ts` as arithmetic on fractions, leave this file as
 * the thinnest possible adapter, and say plainly what is unexercised. The crop geometry, the
 * scaling, the quality ladder and the gate that makes the crop unskippable are all next door and
 * all tested; what is here is `drawImage`, `toBlob` and a `FileReader`.
 *
 * One part of it is reachable after all, and now has a test: {@link decode} is called before any
 * canvas, and a suite can replace the `createImageBitmap` global with one that rejects or remove it
 * altogether, which is the HEIC file and the old WebView. What still cannot be exercised anywhere is
 * the canvas itself and the orientation the decode asks for.
 *
 * `MiraImageTools` is one seam rather than three injected functions, so a screen takes a single
 * prop and a test supplies a single fake.
 */

export interface MiraImageTools {
  /** A URL for showing the chosen file while the box is drawn. Released by {@link release}. */
  readonly previewUrl: (file: File) => string
  readonly release: (url: string) => void
  readonly encode: CropEncoder
}

/**
 * Decode, crop, downscale, re-encode as JPEG, and hand back base64.
 *
 * Four things happen here and three of them are the point:
 *
 * - **The crop.** Only the box's own pixels are drawn, so nothing outside it exists in the output.
 *   This is the whole of `AI-004`'s "stripped of identifying regions": the patient's name is not
 *   blurred or covered, it was never in the file that leaves the device.
 * - **EXIF goes.** A phone writes the camera, the time and often the GPS position into a JPEG. A
 *   canvas re-encode carries none of it, because what is encoded is pixels. Nothing in `AI-004`
 *   asks for that and sending a shop's coordinates to a sub-processor would be a surprise.
 * - **The downscale, then the quality ladder.** Both exist to reach `MIRA_MAX_IMAGE_BYTES` without
 *   blurring the digits, in that order, because scaling a document photograph costs less legibility
 *   than compressing it does.
 *
 * `image/jpeg` is the only output, which is why `MIRA_IMAGE_MEDIA_TYPES` holds one entry.
 */
/**
 * The decode, and the two things about it that are not obvious.
 *
 * **`imageOrientation: 'from-image'` is passed rather than left to the browser.** A phone writes an
 * EXIF Orientation tag instead of rotating the pixels, the `<img>` the box is drawn on honours it
 * (`image-orientation: from-image` is the CSS default), and `createImageBitmap`'s default for the
 * same file is the engine's choice. Where those disagree the bitmap is transposed against the
 * preview and the crop lands on a different part of the slip. Asking for it explicitly makes the two
 * agree whatever the default is. **Unverified on a device**: jsdom has no image decoding, so this
 * needs a real tablet and a photograph with Orientation 6.
 *
 * **Every failure becomes `NotCropped`.** A DOMException from an undecodable file (a current Android
 * phone stores camera output as HEIC and a file picker does not reliably honour the accept filter)
 * and a TypeError from an old WebView with no `createImageBitmap` at all both used to travel out of
 * the screen's `void use()` as an unhandled rejection: the button cleared and nothing appeared.
 * `NotCropped` is the class the screen shows `mira.image.failed` for, which says to try another
 * photograph or type the values in (`AI-005`).
 */
const decode = async (file: File): Promise<ImageBitmap> => {
  if (typeof createImageBitmap !== 'function') {
    throw new NotCropped(
      'This browser cannot decode a photograph, so type the values in instead. createImageBitmap is absent.',
    )
  }
  try {
    return await createImageBitmap(file, { imageOrientation: 'from-image' })
  } catch (error) {
    throw new NotCropped(
      `That photograph could not be decoded on this device (${error instanceof Error ? error.name : 'unknown'}).`,
    )
  }
}

const encode: CropEncoder = async (file, box) => {
  const bitmap = await decode(file)
  try {
    const rect = cropPixels(box, bitmap.width, bitmap.height)
    const size = scaleToFit(rect.width, rect.height, MIRA_MAX_EDGE_PX)
    const canvas = document.createElement('canvas')
    canvas.width = size.width
    canvas.height = size.height
    const context = canvas.getContext('2d')
    if (context === null) {
      throw new NotCropped('This browser cannot crop a photograph, so type the values in instead.')
    }
    context.drawImage(
      bitmap,
      rect.x,
      rect.y,
      rect.width,
      rect.height,
      0,
      0,
      size.width,
      size.height,
    )

    let encoded = 0
    for (const quality of JPEG_QUALITIES) {
      const blob = await toBlob(canvas, quality)
      if (blob === null) continue
      encoded += 1
      if (blob.size <= MIRA_MAX_IMAGE_BYTES) {
        return { mediaType: 'image/jpeg' as MiraImageMediaType, data: await toBase64(blob) }
      }
    }
    if (encoded === 0) {
      // `toBlob` handing back null at every quality is a canvas that cannot write a JPEG, which is
      // not the same thing as a crop that is too large, and saying "draw a smaller box" to someone
      // whose browser cannot encode at all sends them round the loop forever.
      throw new NotCropped('This browser could not encode the photograph, so type the values in.')
    }
    // Below the ladder's floor a pen stroke breaks up, and a model misreading a blurred 3 as an 8
    // is the failure `AI-001` exists for. Refusing is the safe answer and the manual path is whole.
    throw new NotCropped(
      'That crop is too large to send even compressed. Draw a smaller box around just the values.',
    )
  } finally {
    bitmap.close()
  }
}

const toBlob = (canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> =>
  new Promise((resolve) => {
    try {
      canvas.toBlob(resolve, 'image/jpeg', quality)
    } catch {
      // A canvas that refuses the type throws rather than calling back, and a throw here is a
      // rejection nothing was waiting for. Null is what the caller already handles.
      resolve(null)
    }
  })

/**
 * Base64 through a `FileReader` rather than `btoa`.
 *
 * `btoa(String.fromCharCode(...bytes))` overflows the argument limit somewhere above a hundred
 * thousand bytes, and these are a megabyte and a half. A data URL is base64 by definition, so the
 * prefix is cut and the rest is exactly what the provider's `source.data` takes.
 */
const toBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => {
      reject(new NotCropped('That photograph could not be read on this device.'))
    }
    reader.onload = () => {
      const url = String(reader.result)
      const comma = url.indexOf(',')
      if (comma < 0) {
        reject(new NotCropped('That photograph could not be read on this device.'))
        return
      }
      resolve(url.slice(comma + 1))
    }
    reader.readAsDataURL(blob)
  })

export const browserImageTools: MiraImageTools = {
  previewUrl: (file) => URL.createObjectURL(file),
  release: (url) => {
    URL.revokeObjectURL(url)
  },
  encode,
}

export type { CropBox }
